import createTheme from "@mui/material/styles/createTheme";
import {ptPT} from "@mui/material/locale";

import {HEADER_HEIGHT} from "base/ui/app/config/measurements";

import teal from "@mui/material/colors/teal";
import blueGrey from "@mui/material/colors/blueGrey";

// For the palette, only the "main" variant is required. If no other variant is provided, "dark" & "light" variants will be calculated by default through the createTheme method.
// Specific colors can be imported from the MUI color palettes https://mui.com/material-ui/customization/color/ to be used as objects containing all the different hues and shades of that specific palette.

// When no customTheme is available, just export defaultTheme empty object instead in order to use MUI default theme.
// const defaultTheme = {};
// export const theme = defaultTheme;

export const CUSTOM_FONT_FAMILY = "Roboto";

const customHeadingsStyle = {fontFamily: CUSTOM_FONT_FAMILY, fontWeight: "bold"};

// We are defining colors in a global variable so that we can use them in files other than React components (like hooks). If this was not needed, we would just define the colors inside the palette object.
export const CUSTOM_COLORS = {
    primary: {
        main: "#52684b",
        light: "#A3AF9E",
        lighter: "#d7ddd5",
        dark: "#3F4A3C",
        contrastText: "#fff",
        hover: "#d7ddd5",
    },
    secondary: {
        main: "#4e7897",
        light: "#9bb6ca",
        lighter: "#cddae5",
        dark: "#233543",
    },
    error: {
        main: "#d32f2f",
        dark: "#a92323",
    },
    pageBackground: {primary: "#e4e9e2", secondary: "#f1f4f0"},
    text: {
        primary: "#1b2631",
    },
    grey: blueGrey,
    white: "#fff",
};

const customTheme = createTheme({
    palette: {
        primary: {
            main: CUSTOM_COLORS.primary.main,
            lighter: CUSTOM_COLORS.primary.lighter,
        },
        secondary: {
            dark: CUSTOM_COLORS.secondary.dark,
            main: CUSTOM_COLORS.secondary.main,
            light: CUSTOM_COLORS.secondary.light,
            lighter: CUSTOM_COLORS.secondary.lighter,
        },
        error: {
            main: CUSTOM_COLORS.error.main,
            dark: CUSTOM_COLORS.error.dark,
        },
        pageBackground: {
            primary: CUSTOM_COLORS.pageBackground.primary,
            secondary: CUSTOM_COLORS.pageBackground.secondary,
        },
        white: CUSTOM_COLORS.white,
        text: {
            primary: CUSTOM_COLORS.text.primary,
        },
        menu: {
            primary: {
                header: {
                    background: CUSTOM_COLORS.primary.lighter,
                    text: CUSTOM_COLORS.secondary.dark,
                },
                options: {
                    background: CUSTOM_COLORS.secondary.dark,
                    text: CUSTOM_COLORS.primary.light,
                },
            },
            secondary: {
                header: {
                    background: CUSTOM_COLORS.primary.lighter,
                    text: CUSTOM_COLORS.secondary.dark,
                },
                options: {
                    background: CUSTOM_COLORS.white,
                    text: CUSTOM_COLORS.grey[600],
                },
                background: "white",
            },
        },
    },

    typography: {
        fontSize: 14,
    },

    mixins: {
        toolbar: {
            minHeight: `${HEADER_HEIGHT}px`,
        },
    },

    components: {
        MuiTypography: {
            styleOverrides: {
                h1: customHeadingsStyle,
                h2: customHeadingsStyle,
                h3: customHeadingsStyle,
                h4: customHeadingsStyle,
                h5: customHeadingsStyle,
                h6: {
                    ...customHeadingsStyle,
                    color: CUSTOM_COLORS.primary.main,
                    textTransform: "uppercase",
                },
            },
        },

        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: 14,
                },
            },
        },

        MuiListItemButton: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        // <-- mixing the two classes
                        backgroundColor: CUSTOM_COLORS.secondary.light,
                    },
                },
            },
        },

        MuiTableRow: {
            styleOverrides: {
                root: {
                    "&:last-child td, &:last-child th": {
                        borderBottom: 0,
                    },
                },
            },
        },

        MuiTableCell: {
            styleOverrides: {
                head: {
                    lineHeight: "1rem",
                },
            },
        },

        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    lineHeight: "1rem",
                },
            },
        },

        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: CUSTOM_COLORS.white,
                },
                input: {
                    fontSize: "14px",
                    height: "unset",
                    "&::placeholder": {
                        opacity: 0.15,
                        fontStyle: "italic",
                    },
                },
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    padding: "14px 0 14px 14px",
                    backgroundColor: CUSTOM_COLORS.white,
                },
            },
        },

        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    paddingTop: "7px",
                    paddingBottom: "6px",
                },
            },
        },
    },
    // Language for UI localization
    ptPT,
});

export const theme = customTheme;
